import { ReactNode } from 'react';
import { DisplayModeFriends, DisplayUeopt, SettingsType } from '../../../../../../constants/enums';
import { locales } from '../../../../../../locales/en';
import { getModelAnimationDisplayRate } from '../../../../../../mfcLogic/settings';
import { GridDensityUeopt, UEOPT_APP_GRID_DENSITY_OPTIONS, UEOPT_HOME_DISPLAY_TYPES, UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS, UEOPT_MODEL_LIST_SORT_RATES } from '../../../../../../mfcLogic/ueopts';
import { initListChanges } from '../../../../../../state/state.lists';
import { trackAnalyticsEvent } from '../../../../../../utilities/analytics';
import { useRouteSelector } from '../../../../../Hooks/useRoute';
import { useDisplaySetting, useSettingSelector } from '../../../../../Hooks/useSettings';
import PopoverSelect from '../../../../../common/PopoverSelect';
import SegmentedControl from '../../../../../common/SegmentedControl';
import { InlineSelect, SettingRow, SettingsGroup, SettingsSection, getUeoptOptions } from '../../../../../common/Settings';
import { Toggle } from '../../../../../common/Toggle';
import "./View.linaria.module.css!=!./View?./View.linaria.module.css?css=H4sIAAAAAAAAA52Sza7bIBCF932K7txKITK+aZLrrrrrps+AsBnwxBgcmNhOrL57lZtf32upVZecmfMxc2ApqRGvUXAaeuzsqDC2Vh5zbWH4/vvT8lyuBK/Wfnc4TU+jtGgcQ4Im5iU4gnBzlIPQZNBmo5Ftvm0fqPRamJ7GRgaDjgU0FT33y0YYe+x1M54Hyp13cCvpIFC12qxGC0QQWGxlic7k6a2DXm9mgoEYBemi9qGZYI51WAcu+C7b7oZsRvq81AhWsdI7Ct4u/s2VV76D8J9e7ctDZD1She4dYix8UBBY4Yl8w0pvfcjfNmtlAEf3tfqX48CvCX1UblR07YHGViqFzjDy7SM9nm7cemOeEVNpnnEZ7IFRrSr2KExNuvsgzCMuvyBLH9+A+6zCvRJ8X1V776e2SwadDF8YMwHAMYuu/npPgvfrPgru+1rtihlpStPeEdOyQXvMk1/eUYQQJCWL5CfYDghLmSySHwGlTRZRusgiBNT3Sb+tqKw7wXU/uA7mtJn7Ip4g59nTwqtaQRrPlvTlZOa0GUwPb8lt0nv6xWC2mXufW1lBF7z7W3K7QlhbHLZqbNCxHhVVOb8+yx/zsRntOAQAAA=="
const quickAnimationStyles = "atm_9s_1txwivl atm_h_1h6ojuz atm_cx_ftgil2 atm_h0_ftgil2";
const quickSelectStyles = "atm_am_glywfm atm_fr_idpfg4 atm_t9_glywfm atm_ykr6r1_1j28jx2 atm_yw3yx1_idpfg4 atm_107n67g_idpfg4 atm_dpdbqi_gktfv atm_1o2hiqd_1qhhqoo atm_y1w6ws_1owkdjb atm_154tckv_1fwxnve atm_14kde0s_1fw03zg atm_bxg82n_1qhhqoo";
const quickMenuStyles = "atm_jb_llbu8d";
export const typeOptions = getUeoptOptions(UEOPT_HOME_DISPLAY_TYPES, ['avatars', 'previews', 'text', 'detailed'], locales.settings.displayType, {
  avatars: 'SettingsModal__DisplayTypeAvatarOption',
  previews: 'SettingsModal__DisplayTypePreviewOption',
  text: 'SettingsModal__DisplayTypeTextOnlyOption',
  detailed: 'SettingsModal__DisplayTypeDetailedOption'
});
const sizeOptions = getUeoptOptions(UEOPT_APP_GRID_DENSITY_OPTIONS, ['small', 'medium', 'large'], locales.settings.view, {
  small: 'Button__Density_small',
  medium: 'Button__Density_medium',
  large: 'Button__Density_large'
});
export type QuickDisplay = DisplayUeopt | DisplayModeFriends;
export interface DisplayTypeOption {
  'data-testid'?: string;
  label: string;
  value: QuickDisplay;
}
interface ControlsProps {
  display?: QuickDisplay;
  mini?: boolean;
}
const updateRateOptions = getUeoptOptions(UEOPT_MODEL_LIST_SORT_RATES, ['quarterMinute', 'halfMinute', 'minute', 'twoMinutes', 'fiveMinutes', 'tenMinutes'], locales.settings.gridUpdateRate);
const animationRates = [{
  value: 0,
  label: locales.settings.previewAnimationRate.off
}, ...getUeoptOptions(UEOPT_HOME_FEED_ANIMATION_RATE_SPEEDS, ['static', 'slow', 'medium', 'fast'], locales.settings.previewAnimationRate)];
interface DisplayTypeControlsProps {
  display: DisplayUeopt;
  displayChosen: boolean;
  mini: boolean;
  saveDisplay(display: DisplayUeopt): void;
}
const DisplayTypeControls = (props: DisplayTypeControlsProps) => {
  const {
    display,
    displayChosen,
    mini,
    saveDisplay
  } = props;
  const onChangeDisplay = (next: DisplayUeopt) => {
    if (next === display && displayChosen) return;
    trackAnalyticsEvent({
      action: `DisplayType_Change`,
      data: {
        displayType: next,
        previousDisplayType: display
      }
    });
    saveDisplay(next);
  };
  return <SegmentedControl mini={mini} aria-label={locales.settings.displayType.title} options={typeOptions} value={display} onChange={onChangeDisplay} />;
};
const GridRefreshRateRow = () => {
  const [updateRate, saveUpdateRate] = useSettingSelector(SettingsType.DEFAULT, 'modelListSortRate');
  const onChangeUpdateRate = (next: string) => {
    saveUpdateRate(parseInt(next));
    initListChanges();
  };
  return <SettingRow label={locales.settings.gridUpdateRate.title}>
            <InlineSelect data-testid="Settings__GridUpdateRate" name="gridUpdateRate" options={updateRateOptions} value={updateRate} onChange={onChangeUpdateRate} />
        </SettingRow>;
};
const usePreviewSettings = () => {
  const [overlayAvatar = 1, saveOverlayAvatar] = useSettingSelector(SettingsType.DEFAULT, 'overlayAvatarHomepage');
  const [animatePreviews, saveAnimatePreviews] = useSettingSelector(SettingsType.DEFAULT, 'animatePreviews');
  const [animationSpeed, saveAnimationSpeed] = useSettingSelector(SettingsType.DEFAULT, 'animationSpeed');
  const toggleOverlayAvatar = () => saveOverlayAvatar(overlayAvatar === 1 ? 0 : 1);
  const onChangeAnimationRate = (next: string) => {
    const value = parseInt(next) as 0 | 1 | 10 | 100 | 10000;
    if (value === 0) {
      saveAnimatePreviews(0);
      return;
    }
    if (value !== animationSpeed) saveAnimationSpeed(value);
    if (animatePreviews !== 1) saveAnimatePreviews(1);
  };
  const animationRate = animatePreviews === 1 ? getModelAnimationDisplayRate(animationSpeed) : 0;
  return {
    animationRate,
    overlayAvatar,
    onChangeAnimationRate,
    toggleOverlayAvatar
  };
};
const PreviewRows = ({
  display
}: {
  display: QuickDisplay;
}) => {
  const {
    animationRate,
    overlayAvatar,
    onChangeAnimationRate,
    toggleOverlayAvatar
  } = usePreviewSettings();
  return <>
            {display === DisplayUeopt.preview && <SettingRow label={locales.settings.displayType.overlayAvatarOnPreview} data-testid="Settings__OverlayAvatarOnPreview">
                    <Toggle checked={overlayAvatar === 1} name="OverlayAvatarOnPreview" title={locales.settings.displayType.overlayAvatarOnPreview} onToggle={toggleOverlayAvatar} />
                </SettingRow>}
            <SettingRow label={locales.settings.previewAnimationRate.title}>
                <InlineSelect data-testid="Settings__PreviewAnimationRate" name="animationSpeed" options={animationRates} value={animationRate} onChange={onChangeAnimationRate} />
            </SettingRow>
        </>;
};
const QuickAnimation = () => {
  const {
    animationRate,
    onChangeAnimationRate
  } = usePreviewSettings();
  return <span className={quickAnimationStyles}>
            <span>{locales.settings.displayToolbar.animation}</span>
            <PopoverSelect underline className={quickSelectStyles} data-testid="QuickDisplay__AnimationRate" menuClassName={quickMenuStyles} name="quickAnimationSpeed" options={animationRates.map(option => ({
      ...option,
      value: String(option.value)
    }))} value={String(animationRate)} onChange={next => {
      if (next !== String(animationRate)) onChangeAnimationRate(next);
    }} />
        </span>;
};
interface DisplaySizeControlsProps {
  gridDensity?: GridDensityUeopt;
  mini?: boolean;
  onChangeGridDensity?(gridDensity: GridDensityUeopt): void;
}
const DisplaySizeControls = (props: DisplaySizeControlsProps) => {
  const {
    mini = false,
    onChangeGridDensity
  } = props;
  const [gridDensitySetting, saveGridDensitySetting] = useSettingSelector(SettingsType.DEFAULT, 'gridDensity');
  const gridDensity = onChangeGridDensity ? props.gridDensity : gridDensitySetting;
  const saveGridDensity = onChangeGridDensity ?? saveGridDensitySetting;
  return <SegmentedControl mini={mini} aria-label={locales.settings.gridDensity.title} options={sizeOptions} value={gridDensity ?? GridDensityUeopt.medium} onChange={next => {
    if (next !== gridDensity) saveGridDensity(next);
  }} />;
};
interface ViewControlsProps extends ControlsProps {
  aux?: ReactNode;
  displayOptions?: Array<DisplayTypeOption>;
  gridDensity?: GridDensityUeopt;
  title?: ReactNode;
  onChangeDisplay?(display: QuickDisplay): void;
  onChangeGridDensity?(gridDensity: GridDensityUeopt): void;
}
export const ViewControls = (props: ViewControlsProps) => {
  const {
    aux,
    displayOptions,
    gridDensity,
    mini = false,
    title = locales.settings.view.title,
    onChangeDisplay,
    onChangeGridDensity
  } = props;
  const basePage = useRouteSelector('basePage');
  const [displaySetting, saveDisplay, displayChosen] = useDisplaySetting(basePage);
  const display = props.display ?? displaySetting ?? DisplayUeopt.default;
  const hasPreviewOptions = display === DisplayUeopt.preview || display === DisplayUeopt.detailed;
  return <>
            <SettingsSection compact={mini} title={title} aux={mini && hasPreviewOptions ? <>
                            <QuickAnimation />
                            {aux}
                        </> : aux}>
                {onChangeDisplay ? <SegmentedControl mini={mini} aria-label={locales.settings.displayType.title} options={displayOptions} value={display} onChange={onChangeDisplay} /> : <DisplayTypeControls display={display as DisplayUeopt} displayChosen={displayChosen} mini={mini} saveDisplay={saveDisplay} />}
                <DisplaySizeControls gridDensity={gridDensity} mini={mini} onChangeGridDensity={onChangeGridDensity} />
                {!mini && <SettingsGroup>
                        <GridRefreshRateRow />
                        {hasPreviewOptions && <PreviewRows display={display} />}
                    </SettingsGroup>}
            </SettingsSection>
        </>;
};
export default ViewControls;